/* eslint-disable no-underscore-dangle */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import { toast } from 'react-toastify';
import useSafeNavigate from '../../hooks/useSafeNavigate';
import styles from './ManagerDashboard.module.scss';
import { getCookies } from '../../hooks/useCookies';
import { SERVER_BASE_URL } from '../../redux/apiConstants';
import { formatTime } from '../../utilityFunctions/utilsFunctions';
import {
  canEditEvent,
  canDeleteEvent,
  canManageRoles,
  canViewAnalytics,
} from '../../utils/eventPermissions';

// Image assets
import marqLogo from '../../assets/marqLogo.svg';
import nodataImg from '../../assets/nodata_img.svg';
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import PageLoader from '../../components/PageLoader/PageLoader';

const event01 =
  'https://marqproduction.s3.amazonaws.com/undefined/photo-1492684223066-81342ee5ff30.jpeg-1772890279082';
const event02 =
  'https://marqproduction.s3.amazonaws.com/undefined/photo-1492684223066-81342ee5ff30.jpeg-1772890279082';
const event03 =
  'https://marqproduction.s3.amazonaws.com/undefined/photo-1492684223066-81342ee5ff30.jpeg-1772890279082';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

function ManagerDashboard() {
  const navigate = useSafeNavigate();
  const userName = getCookies('USERNAME') || 'User';
  const [dashboardData, setDashboardData] = useState({
    totalShows: 0,
    ticketsSoldToday: 0,
    totalRevenue: 0,
    activeVendorsCount: 0,
    pendingPayouts: 0,
  });
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState({ labels: [], data: [] });
  const [performancePeriod, setPerformancePeriod] = useState('week');
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [revenueTrendData, setRevenueTrendData] = useState({ labels: [], data: [] });
  const [revenueTrendPeriod, setRevenueTrendPeriod] = useState('month');
  const [revenueTrendLoading, setRevenueTrendLoading] = useState(false);
  const errorShownRef = useRef({ dashboard: false, events: false });
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  /** After first successful load, only section loaders show (e.g. upcoming events refetch). */
  const [initialFetchFinished, setInitialFetchFinished] = useState(false);

  useEffect(() => {
    if (!loading && !eventsLoading) {
      setInitialFetchFinished(true);
    }
  }, [loading, eventsLoading]);

  const showInitialPageLoader = !initialFetchFinished;

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = getCookies('Token');
        const res = await axios.get(`${SERVER_BASE_URL}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.code === 200) {
          setUnreadNotificationCount(res.data.data?.count ?? res.data.data?.result ?? 0);
        }
      } catch {
        // silently ignore — notification dot simply stays hidden
      }
    };
    fetchUnreadCount();
  }, []);

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('en-US');
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = getCookies('Token');
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const response = await axios.get(`${SERVER_BASE_URL}/eventdashboard`, {
          headers,
          params: { timezone: userTimeZone },
        });

        if (response.status === 200 && response.data.code === 200) {
          const { data } = response.data;
          setDashboardData({
            totalShows: data.totalShows || 0,
            ticketsSoldToday: data.ticketsSoldToday || 0,
            totalRevenue: data.totalRevenue || 0,
            activeVendorsCount: data.activeVendorsCount || 0,
            pendingPayouts: data.pendingPayouts || 0,
          });
          errorShownRef.current.dashboard = false; // Reset on success
        } else if (!errorShownRef.current.dashboard) {
          errorShownRef.current.dashboard = true;
          toast.error(response.data?.message || 'Failed to load dashboard data');
        }
      } catch (error) {
        if (!errorShownRef.current.dashboard) {
          errorShownRef.current.dashboard = true;
          toast.error(
            error?.response?.data?.message || 'An error occurred while loading dashboard data'
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        setPerformanceLoading(true);
        const token = getCookies('Token');
        const headers = { Authorization: `Bearer ${token}` };
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const response = await axios.get(`${SERVER_BASE_URL}/eventdashboard/performance`, {
          headers,
          params: { period: performancePeriod, timezone: userTimeZone },
        });
        if (response?.data?.code === 200 && response?.data?.data) {
          const { labels = [], data = [] } = response.data.data;
          setPerformanceData({ labels, data });
        }
      } catch (err) {
        setPerformanceData({ labels: [], data: [] });
      } finally {
        setPerformanceLoading(false);
      }
    };
    fetchPerformance();
  }, [performancePeriod]);

  // Revenue trend: get start/end dates for period (month, lastMonth, year)
  const toLocalDateParam = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getRevenueTrendDateRange = (period) => {
    const now = new Date();
    let start;
    let end = new Date(now);
    if (period === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (period === 'lastMonth') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    } else {
      // year
      start = new Date(now.getFullYear(), 0, 1);
    }
    return {
      startDate: toLocalDateParam(start),
      endDate: toLocalDateParam(end),
    };
  };

  // Transform revenue-trend API response (daily) into chart labels + data by week or month
  const transformRevenueTrendToChart = (rawData, period) => {
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return { labels: [], data: [] };
    }
    if (period === 'year') {
      const byMonth = {};
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      rawData.forEach((row) => {
        const monthKey = row.date ? row.date.slice(0, 7) : '';
        if (!monthKey) return;
        if (!byMonth[monthKey]) byMonth[monthKey] = 0;
        byMonth[monthKey] += Number(row.netRevenue) || 0;
      });
      const labels = [];
      const data = [];
      for (let m = 1; m <= 12; m += 1) {
        const key = `${new Date().getFullYear()}-${String(m).padStart(2, '0')}`;
        labels.push(monthNames[m - 1]);
        data.push(byMonth[key] || 0);
      }
      return { labels, data };
    }
    // month or lastMonth: Week 1, Week 2, Week 3, Week 4
    const weekSums = [0, 0, 0, 0];
    rawData.forEach((row) => {
      if (!row.date) return;
      const day = parseInt(row.date.slice(8, 10), 10);
      const weekIndex = Math.min(3, Math.floor((day - 1) / 7));
      weekSums[weekIndex] += Number(row.netRevenue) || 0;
    });
    return {
      labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      data: weekSums,
    };
  };

  useEffect(() => {
    const fetchRevenueTrend = async () => {
      try {
        setRevenueTrendLoading(true);
        const token = getCookies('Token');
        const headers = { Authorization: `Bearer ${token}` };
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const { startDate, endDate } = getRevenueTrendDateRange(revenueTrendPeriod);
        const params = new URLSearchParams({ startDate, endDate, timezone: userTimeZone });
        const response = await axios.get(
          `${SERVER_BASE_URL}/analytics/revenue-trend?${params.toString()}`,
          { headers }
        );
        if (response?.data?.code === 200 && Array.isArray(response?.data?.data?.data)) {
          const chart = transformRevenueTrendToChart(response.data.data.data, revenueTrendPeriod);
          setRevenueTrendData(chart);
        } else {
          setRevenueTrendData({ labels: [], data: [] });
        }
      } catch (err) {
        setRevenueTrendData({ labels: [], data: [] });
      } finally {
        setRevenueTrendLoading(false);
      }
    };
    fetchRevenueTrend();
  }, [revenueTrendPeriod]);

  /** Gross revenue for the manager over the same window as the chart (sum of daily buckets). */
  const revenueTrendPeriodTotal = useMemo(() => {
    const arr = revenueTrendData?.data;
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    return arr.reduce((sum, v) => sum + (Number(v) || 0), 0);
  }, [revenueTrendData]);

  const fetchUpcomingEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      const token = getCookies('Token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const response = await axios.get(`${SERVER_BASE_URL}/shows/upcoming?limit=3&page=1`, {
        headers,
      });

      if (response.status === 200 && response.data.code === 200) {
        const raw = response.data.data?.result?.results || [];
        // Normalize status to handle both "in progress" (space) and "in_progress" (underscore)
        const normalizeStatus = (s) => (s || '').toString().trim().toLowerCase().replace(/_/g, ' ');
        const events = raw.filter((e) => {
          const s = normalizeStatus(e.status);
          return s === 'created' || s === 'in progress';
        });
        setUpcomingEvents(events);
        errorShownRef.current.events = false; // Reset on success
      } else if (!errorShownRef.current.events) {
        errorShownRef.current.events = true;
        toast.error(response.data?.message || 'Failed to load upcoming events');
      }
    } catch (error) {
      if (!errorShownRef.current.events) {
        errorShownRef.current.events = true;
        toast.error(
          error?.response?.data?.message || 'An error occurred while loading upcoming events'
        );
      }
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpcomingEvents();
  }, [fetchUpcomingEvents]);

  // Format date for display (e.g., "March 15, 2025 • 2:00 PM IST")
  const formatEventDate = (startDate, startTime, timezone) => {
    if (!startDate) return 'Date TBD';
    const mDate = timezone ? moment.tz(startDate, timezone) : moment(startDate);
    if (!mDate.isValid()) return 'Date TBD';
    const dateStr = mDate.format('MM-DD-YYYY');
    const tzAbbr = timezone ? mDate.zoneAbbr() : '';
    const timeStr = startTime ? `${formatTime(startTime)}${tzAbbr ? ` ${tzAbbr}` : ''}` : '';
    return timeStr ? `${dateStr} • ${timeStr}` : dateStr;
  };

  // Get event status badge
  const getEventStatus = (event) => {
    const status = event.status || '';
    switch (status) {
      case 'in progress':
        return { text: 'Live', className: styles.badgeLive };
      case 'created':
        return { text: 'Upcoming', className: styles.badgeLive };
      case 'ended':
        return { text: 'Closed', className: styles.badgeDraft };
      case 'draft':
        return { text: 'Draft', className: styles.badgeDraft };
      case 'rejected':
        return { text: 'Rejected', className: styles.badgeDraft };
      default:
        return { text: 'Upcoming', className: styles.badgeLive };
    }
  };

  // Get event image or default
  const getEventImage = (index, event) => {
    if (event.bannerImage || event.image) return event.bannerImage || event.image;
    const defaultImages = [event01, event02, event03];
    return defaultImages[index % defaultImages.length];
  };

  /**
   * Ticket line for upcoming events: use API bookedTicketsCount (sum of numberOfTicketsBooked
   * where paymentStatus is "booked") — same source as the events list. Fallback to legacy fields.
   */
  const getTicketInfo = (event) => {
    const ticketsBooked =
      event.bookedTicketsCount != null
        ? Number(event.bookedTicketsCount)
        : Number(event.ticketsSold || event.totalTicketsSold || event.seatsBooked || 0) || 0;
    const totalCapacityRaw =
      event.totalNumberOfTickets != null
        ? Number(event.totalNumberOfTickets)
        : event.totalCapacity != null
        ? Number(event.totalCapacity)
        : event.maxCapacity != null
        ? Number(event.maxCapacity)
        : 0;
    const totalCapacity =
      Number.isFinite(totalCapacityRaw) && totalCapacityRaw > 0 ? totalCapacityRaw : 0;
    const percentage = totalCapacity > 0 ? Math.round((ticketsBooked / totalCapacity) * 100) : 0;

    if (totalCapacity === 0) {
      return {
        text: `${formatNumber(ticketsBooked)} tickets booked`,
        warning: false,
      };
    }
    if (percentage >= 95) {
      return {
        text: `⚠️ ${formatNumber(ticketsBooked)} / ${formatNumber(
          totalCapacity
        )} booked (${percentage}% of capacity)`,
        warning: true,
      };
    }
    return {
      text: `${formatNumber(ticketsBooked)} / ${formatNumber(totalCapacity)} tickets booked`,
      warning: false,
    };
  };

  // Analytics route per event type (seat-booking, non-seat-booking, registration, token, rsvp)
  const getAnalyticsRoute = (event) => {
    const eventId = event.id || event._id;
    if (!eventId) return null;
    const eventType = (
      event.eventType ||
      event.category ||
      event.eventCategory ||
      ''
    ).toLowerCase();
    if (eventType === 'booking' || eventType === 'seat-booking' || eventType === 'seatbooking') {
      return `/seat-booking-analytics/${eventId}`;
    }
    if (
      eventType === 'non-seat-booking' ||
      eventType === 'nonseatbooking' ||
      eventType === 'general'
    ) {
      return `/non-seat-booking-analytics/${eventId}`;
    }
    if (
      eventType === 'form' ||
      eventType === 'registration' ||
      eventType === 'registration-based'
    ) {
      return `/registration-analytics/${eventId}`;
    }
    if (eventType === 'token-based' || eventType === 'tokenbased' || eventType === 'token') {
      return `/token-based-analytics/${eventId}`;
    }
    if (eventType === 'rsvp') {
      return `/rsvp-analytics/${eventId}`;
    }
    return `/seat-booking-analytics/${eventId}`;
  };

  const currentUserId = getCookies('USERID') || '';
  const isBookingInProgress = (event) =>
    (event?.bookingStatus ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ') ===
    'in progress';
  const getCanEdit = (ev) => canEditEvent(ev, currentUserId);
  const getCanDelete = (ev) => canDeleteEvent(ev, currentUserId);
  const getCanManageRoles = (ev) => canManageRoles(ev, currentUserId);
  const getCanViewAnalytics = (ev) => canViewAnalytics(ev, currentUserId);

  const handleEditEvent = (event) => {
    const eventId = event._id || event.id;
    if (!eventId) return;
    sessionStorage.setItem('marq_create_show_id', String(eventId));
    navigate('/event-basics');
  };

  const handleViewEvent = (event) => {
    const eventId = event._id || event.id;
    if (!eventId) return;
    navigate(`/show-details/${eventId}`, { state: { from: '/manager-dashboard' } });
  };

  const handleDeleteClick = (event) => {
    const eventId = event._id || event.id;
    if (!eventId) return;
    setDeleteConfirmEvent({
      id: eventId,
      eventName: event.eventName || 'this event',
    });
  };

  const handleDeleteClose = () => {
    if (!deleteLoading) setDeleteConfirmEvent(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmEvent) return;
    const { id: eventId, eventName } = deleteConfirmEvent;
    setDeleteLoading(true);
    try {
      const token = getCookies('Token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      await axios.delete(`${SERVER_BASE_URL}/shows/${eventId}`, { headers });
      toast.success(`${eventName} has been disabled`);
      setDeleteConfirmEvent(null);
      fetchUpcomingEvents();
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to delete event';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (showInitialPageLoader) {
    return (
      <div className={styles.managerDashboardWrap}>
        <PageLoader />
      </div>
    );
  }

  return (
    <div className={styles.managerDashboardWrap}>
      <div className={styles.container}>
        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.logo}>
                <img src={marqLogo} alt="MarQ" className={styles.logo} />
              </div>
              <div className={styles.welcomeText}>
                <h1>Welcome back, {userName}!</h1>
                <p>Here&apos;s what&apos;s happening with your events today.</p>
              </div>
            </div>
            <button
              type="button"
              className={styles.notification}
              aria-label="Notifications"
              onClick={() => navigate('/manager-notifications')}
            >
              <svg
                width="21"
                height="22"
                viewBox="0 0 21 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.8349 4.75C10.8349 4.33579 10.4991 4 10.0849 4C9.67072 4 9.33494 4.33579 9.33494 4.75V8.75C9.33494 9.16421 9.67072 9.5 10.0849 9.5C10.4991 9.5 10.8349 9.16421 10.8349 8.75V4.75Z"
                  fill="#4D4D4D"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2.335 7.75C2.335 3.46979 5.80479 0 10.085 0C14.3652 0 17.835 3.46979 17.835 7.75V8.4541C17.835 9.15102 18.0413 9.83235 18.4279 10.4122L19.5765 12.1351C20.9096 14.1348 19.8919 16.8528 17.5733 17.4851C16.8176 17.6912 16.0556 17.8655 15.2891 18.0081L15.2872 18.0132C14.5182 20.0651 12.4629 21.5 10.0849 21.5C7.70694 21.5 5.65163 20.0651 4.88264 18.0132L4.88074 18.0081C4.11433 17.8655 3.35234 17.6912 2.59673 17.4851C0.278136 16.8528 -0.739571 14.1348 0.593525 12.1351L1.74213 10.4122C2.12871 9.83235 2.335 9.15102 2.335 8.4541V7.75ZM6.70845 18.2869C8.9514 18.5549 11.2185 18.555 13.4614 18.287C12.7505 19.3085 11.5139 20 10.0849 20C8.65597 20 7.4194 19.3085 6.70845 18.2869ZM10.085 1.5C6.63322 1.5 3.835 4.29822 3.835 7.75V8.4541C3.835 9.44716 3.54105 10.418 2.9902 11.2443L1.8416 12.9672C1.07643 14.1149 1.66057 15.675 2.9914 16.038C7.63566 17.3046 12.5343 17.3046 17.1786 16.038C18.5094 15.675 19.0936 14.1149 18.3284 12.9672L17.1798 11.2443C16.6289 10.418 16.335 9.44716 16.335 8.4541V7.75C16.335 4.29822 13.5368 1.5 10.085 1.5Z"
                  fill="#4D4D4D"
                />
              </svg>
              {unreadNotificationCount > 0 && <div className={styles.notificationDot} />}
            </button>
          </div>

          {/* Stats Cards */}
          <div className={styles.statsCards}>
            <div className={styles.statsRow}>
              <div className={styles.statItemContainer}>
                <div className={styles.statItem}>
                  <div className={`${styles.statIcon} ${styles.statIconOrange}`}>
                    <svg
                      width="17"
                      height="20"
                      viewBox="0 0 17 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.75293 0C10.0101 0.000247213 10.208 0.210125 10.208 0.459961V3.67969C10.208 5.50962 11.7029 7.00942 13.5146 7.01953C14.2669 7.01953 14.861 7.03027 15.3164 7.03027C15.6233 7.03027 16.1289 7.01953 16.5547 7.01953C16.802 7.01966 16.9999 7.21993 17 7.46973V15.5098C17 17.9897 15.01 19.9999 12.5547 20H4.67285C2.0988 19.9998 2.1043e-05 17.8899 0 15.29V4.50977C0.000126126 2.02987 2.00055 0 4.46582 0H9.75293ZM5.42578 12.9004C5.02001 12.9006 4.68359 13.2308 4.68359 13.6406C4.68372 14.0504 5.02009 14.3904 5.42578 14.3906H10.8125C11.2183 14.3905 11.5546 14.0505 11.5547 13.6406C11.5547 13.2307 11.2184 12.9005 10.8125 12.9004H5.42578ZM5.42578 7.90039C5.02001 7.90061 4.68359 8.24052 4.68359 8.65039C4.68359 9.06026 5.02001 9.39042 5.42578 9.39062H8.77246C9.1784 9.39062 9.51562 9.06039 9.51562 8.65039C9.51562 8.24039 9.1784 7.90039 8.77246 7.90039H5.42578ZM11.6523 0.90625C11.6523 0.475337 12.1698 0.261537 12.4658 0.572266C13.536 1.69615 15.4065 3.66072 16.4521 4.75879C16.7413 5.06179 16.5291 5.56541 16.1123 5.56641C15.2986 5.5694 14.3394 5.56657 13.6494 5.55957C12.5544 5.55957 11.6523 4.64799 11.6523 3.54199V0.90625Z"
                        fill="#FF6A00"
                      />
                    </svg>
                  </div>
                  <div className={styles.statInfo}>
                    <p className={styles.statLabel}>
                      Total <br /> Events
                    </p>
                    <p className={styles.statValue}>
                      {loading ? '...' : formatNumber(dashboardData.totalShows)}
                    </p>
                  </div>
                </div>

                <div className={styles.statDivider} />
              </div>

              <div className={styles.statItemContainer}>
                <div className={styles.statItem}>
                  <div className={`${styles.statIcon} ${styles.statIconPurple}`}>
                    <svg
                      width="20"
                      height="16"
                      viewBox="0 0 20 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.9963 0H12.0037C15.7819 0 17.671 0 18.8448 1.17157C19.7234 2.04858 19.9443 3.32572 19.9998 5.49444C20.007 5.77248 19.7788 5.99467 19.5092 6.06517C18.6493 6.29007 18.0148 7.07108 18.0148 8C18.0148 8.92892 18.6493 9.70993 19.5092 9.93483C19.7788 10.0053 20.007 10.2275 19.9998 10.5056C19.9443 12.6743 19.7234 13.9514 18.8448 14.8284C17.671 16 15.7819 16 12.0037 16H7.9963C4.21809 16 2.32899 16 1.15525 14.8284C0.276613 13.9514 0.0557041 12.6743 0.000162315 10.5056C-0.00695862 10.2275 0.221206 10.0053 0.49076 9.93483C1.35074 9.70993 1.98521 8.92892 1.98521 8C1.98521 7.07108 1.35074 6.29007 0.490761 6.06517C0.221206 5.99467 -0.00695861 5.77248 0.000162328 5.49444C0.0557042 3.32572 0.276613 2.04858 1.15525 1.17157C2.32899 0 4.21809 0 7.9963 0ZM10.9541 6.19887L10.8556 6.02251C10.4749 5.34084 10.2846 5 10 5C9.71542 5 9.52507 5.34084 9.14437 6.02251L9.04588 6.19887C8.9377 6.39258 8.88361 6.48944 8.79927 6.55334C8.71493 6.61725 8.60989 6.64097 8.39981 6.68841L8.20855 6.73161C7.46929 6.89857 7.09965 6.98205 7.01171 7.26432C6.92377 7.54659 7.17576 7.84072 7.67975 8.42898L7.81013 8.58117C7.95335 8.74833 8.02496 8.83191 8.05717 8.93532C8.08939 9.03872 8.07856 9.15023 8.05691 9.37327L8.0372 9.57632C7.961 10.3612 7.9229 10.7536 8.15314 10.9281C8.38337 11.1025 8.72946 10.9435 9.42163 10.6254L9.60071 10.5431C9.7974 10.4527 9.89575 10.4075 10 10.4075C10.1042 10.4075 10.2026 10.4527 10.3993 10.5431L10.5784 10.6254C11.2705 10.9435 11.6166 11.1025 11.8469 10.9281C12.0771 10.7536 12.039 10.3612 11.9628 9.57632L11.9431 9.37327C11.9214 9.15023 11.9106 9.03872 11.9428 8.93532C11.975 8.83191 12.0466 8.74833 12.1899 8.58117L12.3203 8.42898C12.8242 7.84072 13.0762 7.54659 12.9883 7.26432C12.9003 6.98205 12.5307 6.89857 11.7914 6.73161L11.6002 6.68841C11.3901 6.64097 11.2851 6.61725 11.2007 6.55334C11.1164 6.48944 11.0623 6.39258 10.9541 6.19887Z"
                        fill="#551FFF"
                      />
                    </svg>
                  </div>
                  <div className={styles.statInfo}>
                    <p className={styles.statLabel}>
                      Tickets Sold <br /> Today
                    </p>
                    <p className={styles.statValue}>
                      {loading ? '...' : formatNumber(dashboardData.ticketsSoldToday)}
                    </p>
                  </div>
                </div>

                <div className={styles.statDivider} />
              </div>

              <div className={styles.statItemContainer}>
                <div className={styles.statItem}>
                  <div className={`${styles.statIcon} ${styles.statIconBlue}`}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM10.75 4C10.75 3.58579 10.4142 3.25 10 3.25C9.58579 3.25 9.25 3.58579 9.25 4V4.31673C7.61957 4.60867 6.25 5.83361 6.25 7.5C6.25 9.41715 8.06278 10.75 10 10.75C11.3765 10.75 12.25 11.6557 12.25 12.5C12.25 13.3443 11.3765 14.25 10 14.25C8.62351 14.25 7.75 13.3443 7.75 12.5C7.75 12.0858 7.41421 11.75 7 11.75C6.58579 11.75 6.25 12.0858 6.25 12.5C6.25 14.1664 7.61957 15.3913 9.25 15.6833V16C9.25 16.4142 9.58579 16.75 10 16.75C10.4142 16.75 10.75 16.4142 10.75 16V15.6833C12.3804 15.3913 13.75 14.1664 13.75 12.5C13.75 10.5828 11.9372 9.25 10 9.25C8.62351 9.25 7.75 8.34427 7.75 7.5C7.75 6.65573 8.62351 5.75 10 5.75C11.3765 5.75 12.25 6.65573 12.25 7.5C12.25 7.91421 12.5858 8.25 13 8.25C13.4142 8.25 13.75 7.91421 13.75 7.5C13.75 5.83361 12.3804 4.60867 10.75 4.31673V4Z"
                        fill="#00B7FE"
                      />
                    </svg>
                  </div>
                  <div className={styles.statInfo}>
                    <p className={styles.statLabel}>
                      Total <br /> Revenue
                    </p>
                    <p className={styles.statValue}>
                      {loading ? '...' : formatCurrency(dashboardData.totalRevenue)}
                    </p>
                    <p className={styles.statSubtext}>This month (excl. fees)</p>
                  </div>
                </div>

                <div className={styles.statDivider} />
              </div>

              <div className={styles.statItemContainer}>
                <div className={styles.statItem}>
                  <div className={`${styles.statIcon} ${styles.statIconPink}`}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.77791 3.65484C3.59687 4.01573 3.50783 4.46093 3.32975 5.35133L2.73183 8.34093C2.35324 10.2339 3.8011 12 5.73155 12C7.30318 12 8.61911 10.8091 8.77549 9.24527L8.8445 8.55515C8.68141 10.4038 10.1385 12 11.9998 12C13.8737 12 15.338 10.382 15.1515 8.51737L15.2245 9.24527C15.3809 10.8091 16.6968 12 18.2685 12C20.1989 12 21.6468 10.2339 21.2682 8.34093L20.6703 5.35133C20.4922 4.46095 20.4031 4.01573 20.2221 3.65484C19.8406 2.89439 19.1542 2.33168 18.3337 2.10675C17.9443 2 17.4903 2 16.5823 2H14.4998H7.41771C6.50969 2 6.05567 2 5.66628 2.10675C4.84579 2.33168 4.15938 2.89439 3.77791 3.65484Z"
                        fill="#FD2254"
                      />
                      <path
                        d="M18.2685 13.4997C19.0856 13.4997 19.8448 13.2874 20.5 12.9187V13.9997C20.5 17.771 20.5 19.6566 19.3284 20.8282C18.3853 21.7712 16.9796 21.9552 14.5 21.991V18.4998C14.5 17.5651 14.5 17.0978 14.299 16.7498C14.1674 16.5217 13.978 16.3324 13.75 16.2007C13.4019 15.9998 12.9346 15.9998 12 15.9998C11.0654 15.9998 10.5981 15.9998 10.25 16.2007C10.022 16.3324 9.83261 16.5217 9.70096 16.7498C9.5 17.0978 9.5 17.5651 9.5 18.4998V21.991C7.02043 21.9552 5.61466 21.7712 4.67157 20.8282C3.5 19.6566 3.5 17.771 3.5 13.9997V12.9186C4.15524 13.2874 4.91439 13.4997 5.73157 13.4997C6.92864 13.4997 8.02617 13.0361 8.84435 12.2717C9.67168 13.0318 10.7765 13.4997 11.9998 13.4997C13.2232 13.4997 14.3281 13.0317 15.1555 12.2715C15.9737 13.036 17.0713 13.4997 18.2685 13.4997Z"
                        fill="#FD2254"
                      />
                    </svg>
                  </div>
                  <div className={styles.statInfo}>
                    <p className={styles.statLabel}>
                      Active <br /> Vendors
                    </p>
                    <p className={styles.statValue}>
                      {loading ? '...' : formatNumber(dashboardData.activeVendorsCount)}
                    </p>
                  </div>
                </div>

                <div className={styles.statDivider} />
              </div>

              <div className={styles.statItemContainer}>
                <div className={styles.statItem}>
                  <div className={`${styles.statIcon} ${styles.statIconGreen}`}>
                    <svg
                      width="20"
                      height="15"
                      viewBox="0 0 20 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18.9414 3.18881C19.5215 3.76206 19.771 4.48386 19.8877 5.34111C20 6.1668 20 7.21664 20 8.51912V8.62363C20 9.92611 20 10.976 19.8877 11.8016C19.771 12.6589 19.5215 13.3807 18.9414 13.9539C18.3612 14.5272 17.6307 14.7738 16.7632 14.889C15.9276 15 14.8651 15 13.547 15H8.622C7.30387 15 6.24141 15 5.4058 14.889C4.53824 14.7738 3.80777 14.5272 3.22762 13.9539C2.87566 13.6062 2.64535 13.2037 2.49261 12.7495C3.36407 12.8574 4.4422 12.8573 5.68787 12.8573H10.6974C11.979 12.8573 13.0833 12.8574 13.9676 12.7399C14.9154 12.614 15.8238 12.3301 16.5607 11.602C17.2975 10.8739 17.5848 9.97624 17.7123 9.03978C17.8312 8.16597 17.8311 7.07481 17.831 5.80839V5.6203C17.8311 4.38912 17.8311 3.32356 17.7219 2.46234C18.1818 2.61328 18.5893 2.84088 18.9414 3.18881Z"
                        fill="#12DB98"
                      />
                      <path
                        d="M8.19263 4.04765C7.26108 4.04765 6.50591 4.79385 6.50591 5.71433C6.50591 6.63482 7.26108 7.38101 8.19263 7.38101C9.12418 7.38101 9.87934 6.63482 9.87934 5.71433C9.87934 4.79385 9.12418 4.04765 8.19263 4.04765Z"
                        fill="#12DB98"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.846905 0.836845C0 1.67369 0 3.02057 0 5.71433C0 8.4081 0 9.75498 0.846905 10.5918C1.69381 11.4287 3.05688 11.4287 5.78303 11.4287H10.6022C13.3284 11.4287 14.6914 11.4287 15.5384 10.5918C16.3853 9.75498 16.3853 8.4081 16.3853 5.71433C16.3853 3.02057 16.3853 1.67369 15.5384 0.836845C14.6914 0 13.3284 0 10.6022 0H5.78303C3.05688 0 1.69381 0 0.846905 0.836845ZM5.06015 5.71433C5.06015 4.00487 6.46261 2.61907 8.19263 2.61907C9.92265 2.61907 11.3251 4.00487 11.3251 5.71433C11.3251 7.4238 9.92265 8.8096 8.19263 8.8096C6.46261 8.8096 5.06015 7.4238 5.06015 5.71433ZM13.4937 8.3334C13.0945 8.3334 12.7709 8.0136 12.7709 7.61911V3.80956C12.7709 3.41506 13.0945 3.09526 13.4937 3.09526C13.893 3.09526 14.2166 3.41506 14.2166 3.80956V7.61911C14.2166 8.0136 13.893 8.3334 13.4937 8.3334ZM2.16864 7.61911C2.16864 8.0136 2.49228 8.3334 2.89152 8.3334C3.29075 8.3334 3.61439 8.0136 3.61439 7.61911L3.61439 3.80956C3.61439 3.41506 3.29075 3.09526 2.89152 3.09526C2.49228 3.09526 2.16864 3.41506 2.16864 3.80956L2.16864 7.61911Z"
                        fill="#12DB98"
                      />
                    </svg>
                  </div>
                  <div className={styles.statInfo}>
                    <p className={styles.statLabel}>
                      Pending <br /> Payouts
                    </p>
                    <p className={styles.statValue}>
                      {loading ? '...' : formatCurrency(dashboardData.pendingPayouts)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section - Only show if events exist */}
          {!eventsLoading && upcomingEvents.length > 0 && (
            <div className={styles.chartsSection}>
              {/* Event Performance Chart */}
              <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <h3>Event Performance</h3>
                  <div className={styles.selectWrapper}>
                    <select
                      className={styles.chartSelect}
                      value={performancePeriod}
                      onChange={(e) => setPerformancePeriod(e.target.value)}
                      aria-label="Select timeframe"
                    >
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="year">This Year</option>
                    </select>
                    <svg
                      className={styles.selectArrow}
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="#4D4D4D"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <div className={styles.chartWrapper}>
                  {performanceLoading ? (
                    <p className={styles.chartLoading}>Loading...</p>
                  ) : performanceData.labels.length === 0 && performanceData.data.length === 0 ? (
                    <>
                      <p className={styles.chartLabel}>No data for this period</p>
                      <p className={styles.chartSubtext}>
                        Event performance (tickets sold) will appear here
                      </p>
                    </>
                  ) : (
                    <Line
                      data={{
                        labels: performanceData.labels,
                        datasets: [
                          {
                            label: 'Tickets Sold',
                            data: performanceData.data,
                            borderColor: '#CD47F3',
                            backgroundColor: 'rgba(205, 71, 243, 0.2)',
                            fill: true,
                            tension: 0.35,
                            pointBackgroundColor: '#CD47F3',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          intersect: false,
                          mode: 'index',
                        },
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => `Tickets Sold ${ctx.raw}`,
                            },
                          },
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: {
                              font: { family: 'Lufga, sans-serif', size: 12 },
                              color: '#6B7280',
                            },
                          },
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Tickets Sold',
                              font: { family: 'Lufga, sans-serif', size: 12 },
                              color: '#6B7280',
                            },
                            grid: { color: '#E5E7EB' },
                            ticks: {
                              font: { family: 'Lufga, sans-serif', size: 12 },
                              color: '#6B7280',
                            },
                          },
                        },
                      }}
                      height={260}
                    />
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className={styles.quickActionsCard}>
                <h3>Quick Actions</h3>
                <div className={styles.quickActionsList}>
                  <button
                    type="button"
                    className={styles.quickActionBtn}
                    onClick={() => navigate('/ai-prompt')}
                  >
                    <div className={`${styles.actionIcon} ${styles.actionIconBlue}`}>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 11 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.14983 0.819978C6.14983 0.366427 5.7834 0 5.32985 0C4.8763 0 4.50988 0.366427 4.50988 0.819978V4.50988H0.819978C0.366427 4.50988 0 4.8763 0 5.32985C0 5.7834 0.366427 6.14983 0.819978 6.14983H4.50988V9.83973C4.50988 10.2933 4.8763 10.6597 5.32985 10.6597C5.7834 10.6597 6.14983 10.2933 6.14983 9.83973V6.14983H9.83973C10.2933 6.14983 10.6597 5.7834 10.6597 5.32985C10.6597 4.8763 10.2933 4.50988 9.83973 4.50988H6.14983V0.819978Z"
                          fill="#3B82F6"
                        />
                      </svg>
                    </div>
                    <span>Create Event</span>
                  </button>

                  <button
                    type="button"
                    className={styles.quickActionBtn}
                    onClick={() => navigate('/events')}
                  >
                    <div className={`${styles.actionIcon} ${styles.actionIconGreen}`}>
                      <svg
                        width="17"
                        height="14"
                        viewBox="0 0 17 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_9674_471)">
                          <path
                            d="M2.45993 3.27991C2.45993 2.41002 2.80549 1.57577 3.4206 0.960663C4.0357 0.345561 4.86996 0 5.73984 0C6.60973 0 7.44399 0.345561 8.05909 0.960663C8.67419 1.57577 9.01975 2.41002 9.01975 3.27991C9.01975 4.1498 8.67419 4.98405 8.05909 5.59916C7.44399 6.21426 6.60973 6.55982 5.73984 6.55982C4.86996 6.55982 4.0357 6.21426 3.4206 5.59916C2.80549 4.98405 2.45993 4.1498 2.45993 3.27991ZM0 12.3586C0 9.83461 2.04482 7.78979 4.56881 7.78979H6.91087C9.43487 7.78979 11.4797 9.83461 11.4797 12.3586C11.4797 12.7788 11.1389 13.1196 10.7186 13.1196H0.761042C0.340803 13.1196 0 12.7788 0 12.3586ZM12.9146 7.99478V6.35483H11.2747C10.9339 6.35483 10.6597 6.08065 10.6597 5.73984C10.6597 5.39904 10.9339 5.12486 11.2747 5.12486H12.9146V3.4849C12.9146 3.1441 13.1888 2.86992 13.5296 2.86992C13.8704 2.86992 14.1446 3.1441 14.1446 3.4849V5.12486H15.7846C16.1254 5.12486 16.3995 5.39904 16.3995 5.73984C16.3995 6.08065 16.1254 6.35483 15.7846 6.35483H14.1446V7.99478C14.1446 8.33558 13.8704 8.60976 13.5296 8.60976C13.1888 8.60976 12.9146 8.33558 12.9146 7.99478Z"
                            fill="#10B981"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_9674_471">
                            <path d="M0 0H16.3995V13.1196H0V0Z" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <span>Invite Team Member</span>
                  </button>

                  <button
                    type="button"
                    className={styles.quickActionBtn}
                    onClick={() => navigate('/payout')}
                  >
                    <div className={`${styles.actionIcon} ${styles.actionIconPurple}`}>
                      <svg
                        width="14"
                        height="12"
                        viewBox="0 0 14 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0.819978 0C1.27353 0 1.63996 0.366427 1.63996 0.819977V9.42974C1.63996 9.65523 1.82445 9.83973 2.04994 9.83973H12.2997C12.7532 9.83973 13.1196 10.2062 13.1196 10.6597C13.1196 11.1133 12.7532 11.4797 12.2997 11.4797H2.04994C0.91735 11.4797 0 10.5623 0 9.42974V0.819977C0 0.366427 0.366427 0 0.819978 0ZM3.27991 2.45993C3.27991 2.00638 3.64634 1.63995 4.09989 1.63995H9.01975C9.4733 1.63995 9.83973 2.00638 9.83973 2.45993C9.83973 2.91348 9.4733 3.27991 9.01975 3.27991H4.09989C3.64634 3.27991 3.27991 2.91348 3.27991 2.45993ZM4.09989 4.09989H7.3798C7.83335 4.09989 8.19978 4.46631 8.19978 4.91986C8.19978 5.37341 7.83335 5.73984 7.3798 5.73984H4.09989C3.64634 5.73984 3.27991 5.37341 3.27991 4.91986C3.27991 4.46631 3.64634 4.09989 4.09989 4.09989ZM4.09989 6.55982H10.6597C11.1133 6.55982 11.4797 6.92625 11.4797 7.3798C11.4797 7.83335 11.1133 8.19977 10.6597 8.19977H4.09989C3.64634 8.19977 3.27991 7.83335 3.27991 7.3798C3.27991 6.92625 3.64634 6.55982 4.09989 6.55982Z"
                          fill="#8B5CF6"
                        />
                      </svg>
                    </div>
                    <span>View Reports</span>
                  </button>

                  <button
                    type="button"
                    className={styles.quickActionBtn}
                    onClick={() => navigate('/vendors')}
                  >
                    <div className={`${styles.actionIcon} ${styles.actionIconOrange}`}>
                      <svg
                        width="17"
                        height="10"
                        viewBox="0 0 17 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.2869 0.543235L5.80647 2.55218C5.39391 2.8853 5.31448 3.4849 5.6271 3.91283C5.95765 4.36894 6.60082 4.45863 7.04412 4.1127L9.58861 2.1345C9.76798 1.99613 10.0242 2.02688 10.1652 2.20625C10.3061 2.38562 10.2728 2.64187 10.0934 2.7828L9.55786 3.19791L13.1196 6.47782V1.63996H13.1017L13.0018 1.57589L11.1414 0.384364C10.7494 0.133246 10.2907 0 9.82436 0C9.26575 0 8.72251 0.192182 8.2869 0.543235ZM8.87113 3.7309L7.54636 4.76099C6.73919 5.39135 5.56816 5.22736 4.96343 4.39713C4.39457 3.61559 4.53806 2.52399 5.28885 1.9167L7.4208 0.192182C7.12355 0.0666232 6.80325 0.00256239 6.47782 0.00256239C5.99609 -3.91378e-08 5.52716 0.143496 5.12486 0.409989L3.27991 1.63996V7.3798H4.00251L6.34458 9.51686C6.84681 9.97554 7.62323 9.93966 8.0819 9.43743C8.22284 9.28112 8.31765 9.09919 8.36633 8.90957L8.80195 9.30931C9.30162 9.76798 10.0806 9.73467 10.5393 9.235C10.6546 9.10944 10.7391 8.96338 10.793 8.8122C11.2901 9.14531 11.9665 9.07613 12.3842 8.62001C12.8429 8.12034 12.8096 7.34136 12.3099 6.88269L8.87113 3.7309ZM0.409989 1.63996C0.184495 1.63996 0 1.82445 0 2.04994V7.3798C0 7.83335 0.366427 8.19978 0.819977 8.19978H1.63995C2.0935 8.19978 2.45993 7.83335 2.45993 7.3798V1.63996H0.409989ZM1.22997 6.55982C1.3387 6.55982 1.44298 6.60301 1.51987 6.6799C1.59676 6.75679 1.63995 6.86107 1.63995 6.96981C1.63995 7.07854 1.59676 7.18283 1.51987 7.25972C1.44298 7.3366 1.3387 7.3798 1.22997 7.3798C1.12123 7.3798 1.01695 7.3366 0.94006 7.25972C0.863173 7.18283 0.819977 7.07854 0.819977 6.96981C0.819977 6.86107 0.863173 6.75679 0.94006 6.6799C1.01695 6.60301 1.12123 6.55982 1.22997 6.55982ZM13.9396 1.63996V7.3798C13.9396 7.83335 14.306 8.19978 14.7596 8.19978H15.5796C16.0331 8.19978 16.3995 7.83335 16.3995 7.3798V2.04994C16.3995 1.82445 16.2151 1.63996 15.9896 1.63996H13.9396ZM14.7596 6.96981C14.7596 6.86107 14.8028 6.75679 14.8797 6.6799C14.9566 6.60301 15.0608 6.55982 15.1696 6.55982C15.2783 6.55982 15.3826 6.60301 15.4595 6.6799C15.5364 6.75679 15.5796 6.86107 15.5796 6.96981C15.5796 7.07854 15.5364 7.18283 15.4595 7.25972C15.3826 7.3366 15.2783 7.3798 15.1696 7.3798C15.0608 7.3798 14.9566 7.3366 14.8797 7.25972C14.8028 7.18283 14.7596 7.07854 14.7596 6.96981Z"
                          fill="#F59E0B"
                        />
                      </svg>
                    </div>
                    <span>Manage Vendors</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bottom Section — show while upcoming events load, or when there are events (revenue + list) */}
          {(eventsLoading || upcomingEvents.length > 0) && (
            <div className={styles.bottomSection}>
              {/* Revenue Trend */}
              <div className={styles.revenueTrendCard}>
                <div className={styles.chartHeader}>
                  <h3>Revenue Trend</h3>
                  <div className={styles.selectWrapper}>
                    <select
                      className={styles.chartSelect}
                      value={revenueTrendPeriod}
                      onChange={(e) => setRevenueTrendPeriod(e.target.value)}
                      aria-label="Revenue trend timeframe"
                    >
                      <option value="month">This Month</option>
                      <option value="lastMonth">Last Month</option>
                      <option value="year">This Year</option>
                    </select>
                    <svg
                      className={styles.selectArrow}
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="#4D4D4D"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <div className={styles.chartWrapper}>
                  {revenueTrendLoading ? (
                    <p className={styles.chartLoading}>Loading...</p>
                  ) : revenueTrendData.labels.length === 0 && revenueTrendData.data.length === 0 ? (
                    <>
                      <p className={styles.chartLabel}>No data for this period</p>
                      <p className={styles.chartSubtext}>Revenue trend will appear here</p>
                    </>
                  ) : (
                    <Line
                      data={{
                        labels: revenueTrendData.labels,
                        datasets: [
                          {
                            label: 'Revenue',
                            data: revenueTrendData.data,
                            borderColor: '#3B82F6',
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            fill: true,
                            tension: 0.35,
                            pointBackgroundColor: '#3B82F6',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { intersect: false, mode: 'index' },
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => `Revenue ${formatCurrency(ctx.raw)}`,
                            },
                          },
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: {
                              font: { family: 'Lufga, sans-serif', size: 12 },
                              color: '#6B7280',
                            },
                          },
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Revenue',
                              font: { family: 'Lufga, sans-serif', size: 12 },
                              color: '#6B7280',
                            },
                            grid: { color: '#E5E7EB' },
                            ticks: {
                              font: { family: 'Lufga, sans-serif', size: 12 },
                              color: '#6B7280',
                              callback: (value) => (value >= 1000 ? `${value / 1000}k` : value),
                            },
                          },
                        },
                      }}
                      height={260}
                    />
                  )}
                </div>
                <div className={styles.revenueDivider} />
                <div className={styles.revenueFooter}>
                  <div className={styles.revenueInfo}>
                    <p className={styles.revenueLabel}>Net Payout Available</p>
                    <p className={styles.revenueValue}>
                      {revenueTrendLoading ? '...' : formatCurrency(revenueTrendPeriodTotal)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Upcoming Events */}
              <div className={styles.upcomingEventsCard}>
                <div className={styles.eventsHeader}>
                  <h3>Upcoming Events</h3>
                  <button
                    type="button"
                    className={styles.viewAllLink}
                    onClick={() => navigate('/events')}
                  >
                    View all →
                  </button>
                </div>
                {eventsLoading ? (
                  <div className={styles.pageLoader} role="status" aria-label="Loading">
                    <p className={styles.pageLoaderText}>Loading...</p>
                  </div>
                ) : (
                  <div className={styles.eventsList}>
                    {upcomingEvents.length === 0 ? (
                      <p style={{ padding: '20px', textAlign: 'center', color: '#736b66' }}>
                        No upcoming events
                      </p>
                    ) : (
                      upcomingEvents.map((event, index) => {
                        const statusBadge = getEventStatus(event);
                        const ticketInfo = getTicketInfo(event);
                        const eventId = event._id || event.id;
                        return (
                          <div key={eventId || index} className={styles.eventCard}>
                            <img
                              src={getEventImage(index, event)}
                              alt={event.eventName || 'Event'}
                              className={styles.eventImage}
                              style={{
                                width: '60px',
                                height: '107px',
                                minWidth: '60px',
                                minHeight: '107px',
                                aspectRatio: '9/16',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                flexShrink: 0,
                              }}
                            />
                            <div className={styles.eventDetails}>
                              <div className={styles.eventTitleRow}>
                                <h4>{event.eventName || 'Untitled Event'}</h4>
                                <span className={statusBadge.className}>{statusBadge.text}</span>
                              </div>
                              <p className={styles.eventDate}>
                                {formatEventDate(
                                  event.startDate,
                                  event.startTime,
                                  event.timezone || event.timezoneCode
                                )}
                              </p>
                              {ticketInfo.warning ? (
                                <p className={styles.eventWarning}>{ticketInfo.text}</p>
                              ) : (
                                <p className={styles.eventTickets}>{ticketInfo.text}</p>
                              )}
                            </div>
                            <div className={styles.eventActions}>
                              {!isBookingInProgress(event) && getCanEdit(event) && (
                                <button
                                  type="button"
                                  className={styles.eventActionBtn}
                                  onClick={() => handleEditEvent(event)}
                                  aria-label="Edit event"
                                  title="Edit event"
                                  data-tooltip="Edit event"
                                >
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M16.5 7.875V9C16.5 12.5355 16.5 14.3033 15.4017 15.4017C14.3033 16.5 12.5355 16.5 9 16.5C5.46447 16.5 3.6967 16.5 2.59835 15.4017C1.5 14.3033 1.5 12.5355 1.5 9C1.5 5.46447 1.5 3.6967 2.59835 2.59835C3.6967 1.5 5.46447 1.5 9 1.5H10.125"
                                      stroke="#2A2A2A"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M12.489 2.5913L12.9756 2.10468C13.7819 1.29844 15.0891 1.29844 15.8953 2.10468C16.7016 2.91093 16.7016 4.21811 15.8953 5.02435L15.4087 5.51097M12.489 2.5913C12.489 2.5913 12.5499 3.62535 13.4623 4.53774C14.3747 5.45014 15.4087 5.51097 15.4087 5.51097M12.489 2.5913L8.01538 7.06495C7.71237 7.36796 7.56087 7.51946 7.43057 7.68651C7.27687 7.88357 7.1451 8.09679 7.03758 8.32239C6.94644 8.51363 6.87868 8.7169 6.74317 9.12343L6.30931 10.425M15.4087 5.51097L10.9351 9.98462C10.632 10.2876 10.4805 10.4391 10.3135 10.5694C10.1164 10.7231 9.90321 10.8549 9.67761 10.9624C9.48637 11.0536 9.2831 11.1213 8.87657 11.2568L7.575 11.6907M7.575 11.6907L6.73282 11.9714C6.53276 12.0381 6.3122 11.986 6.16308 11.8369C6.01397 11.6878 5.9619 11.4672 6.02859 11.2672L6.30931 10.425M7.575 11.6907L6.30931 10.425"
                                      stroke="#2A2A2A"
                                    />
                                  </svg>
                                </button>
                              )}
                              <button
                                type="button"
                                className={styles.eventActionBtn}
                                onClick={() => handleViewEvent(event)}
                                aria-label="View event"
                                title="View event"
                                data-tooltip="View event"
                              >
                                <svg
                                  width="22"
                                  height="18"
                                  viewBox="0 0 22 18"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M2.70646 11.5907C1.89903 10.5555 1.49531 10.0379 1.49531 8.50098C1.49531 6.96406 1.89903 6.44647 2.70646 5.41129C4.31868 3.34431 7.02252 1.00098 10.9953 1.00098C14.9681 1.00098 17.6719 3.34431 19.2842 5.41129C20.0916 6.44647 20.4953 6.96406 20.4953 8.50098C20.4953 10.0379 20.0916 10.5555 19.2842 11.5907C17.6719 13.6576 14.9681 16.001 10.9953 16.001C7.02252 16.001 4.31868 13.6576 2.70646 11.5907Z"
                                    stroke="#2A2A2A"
                                  />
                                  <path
                                    d="M13.4953 8.50098C13.4953 9.88169 12.376 11.001 10.9953 11.001C9.6146 11.001 8.49531 9.88169 8.49531 8.50098C8.49531 7.12026 9.6146 6.00098 10.9953 6.00098C12.376 6.00098 13.4953 7.12026 13.4953 8.50098Z"
                                    stroke="#2A2A2A"
                                  />
                                </svg>
                              </button>
                              {getAnalyticsRoute(event) && (
                                <button
                                  type="button"
                                  className={styles.eventActionBtn}
                                  disabled={!getCanViewAnalytics(event)}
                                  onClick={() => {
                                    if (!getCanViewAnalytics(event)) return;
                                    const route = getAnalyticsRoute(event);
                                    if (route) navigate(route);
                                  }}
                                  aria-label={
                                    getCanViewAnalytics(event)
                                      ? 'View analytics'
                                      : 'View analytics (not available for your role)'
                                  }
                                  title={
                                    getCanViewAnalytics(event)
                                      ? 'View analytics'
                                      : 'Not available for your role'
                                  }
                                  data-tooltip={
                                    getCanViewAnalytics(event)
                                      ? 'View analytics'
                                      : 'Not available for your role'
                                  }
                                >
                                  <svg
                                    width="17"
                                    height="17"
                                    viewBox="0 0 17 17"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M4.22253 13.238C3.91503 13.238 3.66003 12.983 3.66003 12.6755V11.123C3.66003 10.8155 3.91503 10.5605 4.22253 10.5605C4.53003 10.5605 4.78503 10.8155 4.78503 11.123V12.6755C4.78503 12.9905 4.53003 13.238 4.22253 13.238Z"
                                      fill="#2A2A2A"
                                    />
                                    <path
                                      d="M8.0625 13.2375C7.755 13.2375 7.5 12.9825 7.5 12.675V9.5625C7.5 9.255 7.755 9 8.0625 9C8.37 9 8.625 9.255 8.625 9.5625V12.675C8.625 12.99 8.37 13.2375 8.0625 13.2375Z"
                                      fill="#2A2A2A"
                                    />
                                    <path
                                      d="M11.9025 13.2373C11.595 13.2373 11.34 12.9823 11.34 12.6748V8.00977C11.34 7.70227 11.595 7.44727 11.9025 7.44727C12.21 7.44727 12.465 7.70227 12.465 8.00977V12.6748C12.465 12.9898 12.2175 13.2373 11.9025 13.2373Z"
                                      fill="#2A2A2A"
                                    />
                                    <path
                                      d="M4.22243 8.94756C3.96743 8.94756 3.74243 8.77506 3.67493 8.52006C3.59993 8.22006 3.77993 7.91256 4.08743 7.83756C6.84743 7.14756 9.27743 5.64006 11.1299 3.48756L11.4749 3.08256C11.6774 2.85006 12.0299 2.82006 12.2699 3.02256C12.5024 3.22506 12.5324 3.57756 12.3299 3.81756L11.9849 4.22256C9.98243 6.56256 7.34243 8.19006 4.35743 8.93256C4.31243 8.94756 4.26743 8.94756 4.22243 8.94756Z"
                                      fill="#2A2A2A"
                                    />
                                    <path
                                      d="M11.9025 6.20172C11.595 6.20172 11.34 5.94672 11.34 5.63922V4.01172H9.70496C9.39746 4.01172 9.14246 3.75672 9.14246 3.44922C9.14246 3.14172 9.39746 2.88672 9.70496 2.88672H11.9025C12.21 2.88672 12.465 3.14172 12.465 3.44922V5.64672C12.465 5.95422 12.2175 6.20172 11.9025 6.20172Z"
                                      fill="#2A2A2A"
                                    />
                                    <path
                                      d="M10.3125 16.125H5.8125C1.74 16.125 0 14.385 0 10.3125V5.8125C0 1.74 1.74 0 5.8125 0H10.3125C14.385 0 16.125 1.74 16.125 5.8125V10.3125C16.125 14.385 14.385 16.125 10.3125 16.125ZM5.8125 1.125C2.355 1.125 1.125 2.355 1.125 5.8125V10.3125C1.125 13.77 2.355 15 5.8125 15H10.3125C13.77 15 15 13.77 15 10.3125V5.8125C15 2.355 13.77 1.125 10.3125 1.125H5.8125Z"
                                      fill="#2A2A2A"
                                    />
                                  </svg>
                                </button>
                              )}
                              <button
                                type="button"
                                className={styles.eventActionBtn}
                                disabled={!getCanManageRoles(event)}
                                aria-label={
                                  getCanManageRoles(event)
                                    ? 'Roles and Permission'
                                    : 'Roles and Permission (not available for your role)'
                                }
                                title={
                                  getCanManageRoles(event)
                                    ? 'Roles and Permission'
                                    : 'Not available for your role'
                                }
                                data-tooltip={
                                  getCanManageRoles(event)
                                    ? 'Roles and Permission'
                                    : 'Not available for your role'
                                }
                                data-tooltip-align="right"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (eventId) navigate(`/roles-and-permission/${eventId}`);
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M5.66997 7.7775C5.64747 7.7775 5.63247 7.7775 5.60997 7.7775C5.57247 7.77 5.51997 7.77 5.47497 7.7775C3.29997 7.71 1.65747 6 1.65747 3.8925C1.65747 1.7475 3.40497 0 5.54997 0C7.69497 0 9.44247 1.7475 9.44247 3.8925C9.43497 6 7.78497 7.71 5.69247 7.7775C5.68497 7.7775 5.67747 7.7775 5.66997 7.7775ZM5.54997 1.125C4.02747 1.125 2.78247 2.37 2.78247 3.8925C2.78247 5.3925 3.95247 6.6 5.44497 6.6525C5.48997 6.645 5.58747 6.645 5.68497 6.6525C7.15497 6.585 8.30997 5.3775 8.31747 3.8925C8.31747 2.37 7.07247 1.125 5.54997 1.125Z"
                                    fill="#2A2A2A"
                                  />
                                  <path
                                    d="M11.205 7.875C11.1825 7.875 11.16 7.875 11.1375 7.8675C10.83 7.8975 10.515 7.68 10.485 7.3725C10.455 7.065 10.6425 6.7875 10.95 6.75C11.04 6.7425 11.1375 6.7425 11.22 6.7425C12.315 6.6825 13.17 5.7825 13.17 4.68C13.17 3.54 12.2475 2.6175 11.1075 2.6175C10.8 2.625 10.545 2.37 10.545 2.0625C10.545 1.755 10.8 1.5 11.1075 1.5C12.8625 1.5 14.295 2.9325 14.295 4.6875C14.295 6.4125 12.945 7.8075 11.2275 7.875C11.22 7.875 11.2125 7.875 11.205 7.875Z"
                                    fill="#2A2A2A"
                                  />
                                  <path
                                    d="M5.6775 15.975C4.2075 15.975 2.73 15.6 1.6125 14.85C0.57 14.16 0 13.215 0 12.1875C0 11.16 0.57 10.2075 1.6125 9.51C3.8625 8.0175 7.5075 8.0175 9.7425 9.51C10.7775 10.2 11.355 11.145 11.355 12.1725C11.355 13.2 10.785 14.1525 9.7425 14.85C8.6175 15.6 7.1475 15.975 5.6775 15.975ZM2.235 10.455C1.515 10.935 1.125 11.55 1.125 12.195C1.125 12.8325 1.5225 13.4475 2.235 13.92C4.1025 15.1725 7.2525 15.1725 9.12 13.92C9.84 13.44 10.23 12.825 10.23 12.18C10.23 11.5425 9.8325 10.9275 9.12 10.455C7.2525 9.21 4.1025 9.21 2.235 10.455Z"
                                    fill="#2A2A2A"
                                  />
                                  <path
                                    d="M12.555 14.625C12.2925 14.625 12.06 14.445 12.0075 14.175C11.9475 13.8675 12.1425 13.575 12.4425 13.5075C12.915 13.41 13.35 13.2225 13.6875 12.96C14.115 12.6375 14.3475 12.2325 14.3475 11.805C14.3475 11.3775 14.115 10.9725 13.695 10.6575C13.365 10.4025 12.9525 10.2225 12.465 10.11C12.165 10.0425 11.97 9.74251 12.0375 9.43501C12.105 9.13501 12.405 8.94001 12.7125 9.00751C13.3575 9.15001 13.92 9.40501 14.3775 9.75751C15.075 10.2825 15.4725 11.025 15.4725 11.805C15.4725 12.585 15.0675 13.3275 14.37 13.86C13.905 14.22 13.32 14.4825 12.675 14.61C12.63 14.625 12.5925 14.625 12.555 14.625Z"
                                    fill="#2A2A2A"
                                  />
                                </svg>
                              </button>
                              {!isBookingInProgress(event) && getCanDelete(event) && (
                                <button
                                  type="button"
                                  className={styles.eventActionBtn}
                                  onClick={() => handleDeleteClick(event)}
                                  aria-label="Delete event"
                                  title="Delete event"
                                  data-tooltip="Delete event"
                                  data-tooltip-align="right"
                                >
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M6.875 3C7.18388 2.12611 8.01731 1.5 8.99697 1.5C9.97663 1.5 10.8101 2.12611 11.1189 3"
                                      stroke="#B62727"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M15.3751 4.5H2.625"
                                      stroke="#B62727"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M14.125 6.375L13.78 11.5493C13.6473 13.5405 13.5809 14.5361 12.9322 15.1431C12.2834 15.75 11.2856 15.75 9.29001 15.75H8.70999C6.71439 15.75 5.71659 15.75 5.06783 15.1431C4.41907 14.5361 4.3527 13.5405 4.21996 11.5493L3.875 6.375"
                                      stroke="#B62727"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M7.125 8.25L7.5 12"
                                      stroke="#B62727"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M10.875 8.25L10.5 12"
                                      stroke="#B62727"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Empty State - No Data - Only show when no events */}
          {!eventsLoading && upcomingEvents.length === 0 && (
            <div className={styles.emptyStateCard}>
              <div className={styles.emptyStateContent}>
                <div className={styles.emptyStateIcon}>
                  <img src={nodataImg} alt="No Data" className={styles.emptyStateIconImg} />
                </div>
                <h3 className={styles.emptyStateTitle}>No Data</h3>
                <p className={styles.emptyStateDescription}>
                  Event insights will appear once your events are active
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteModal
        show={Boolean(deleteConfirmEvent)}
        message={
          deleteConfirmEvent
            ? `Are you sure you want to delete "${deleteConfirmEvent.eventName}"? This action cannot be undone.`
            : ''
        }
        handleClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteClose}
        loading={deleteLoading}
      />
    </div>
  );
}

export default ManagerDashboard;
